import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, TimerOff } from 'lucide-react';
import { authApi } from '@/services/auth';
import { useAuth } from '@/store/auth';
import { ApiError, errorMessage } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  workspace: z.string().trim().min(3, 'Enter your workspace').toLowerCase(),
  email: z.string().trim().email('Enter a valid email'),
  password: z.string().min(1, 'Enter your password'),
});
type Form = z.infer<typeof schema>;

function lastWorkspace() {
  try {
    return localStorage.getItem('stoplord-workspace') ?? '';
  } catch {
    return '';
  }
}

const REASONS: Record<string, string> = {
  idle: 'You were signed out after a period of inactivity, as required by your company’s idle policy.',
  revoked: 'Your session was ended (signed out elsewhere, by an administrator, or after a password change).',
  logout: 'You have signed out.',
};

export function LoginPage() {
  const status = useAuth((s) => s.status);
  const reason = useAuth((s) => s.signOutReason);
  const navigate = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<Form>({ resolver: zodResolver(schema), defaultValues: { workspace: lastWorkspace(), email: '', password: '' } });

  if (status === 'authenticated') return <Navigate to={loc.state?.from ?? '/'} replace />;

  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      await authApi.login(v);
      navigate(loc.state?.from ?? '/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError && e.status === 429 ? 'Too many attempts. Please wait a few minutes and try again.' : errorMessage(e));
    }
  });

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back. Sign in to your company workspace.">
      {reason && REASONS[reason] && (
        <div className="mb-4 flex gap-2 rounded-lg border border-line bg-surface-muted p-3 text-sm text-ink-muted" role="status">
          {reason === 'idle' && <TimerOff className="mt-0.5 h-4 w-4 shrink-0 text-warning" />}
          {REASONS[reason]}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Workspace" error={formState.errors.workspace?.message} hint="The workspace name your company registered, e.g. “acme”.">
          {(p) => <Input {...p} autoComplete="organization" autoCapitalize="none" {...register('workspace')} />}
        </Field>
        <Field label="Email" error={formState.errors.email?.message}>
          {(p) => <Input {...p} type="email" autoComplete="email" {...register('email')} />}
        </Field>
        <Field label="Password" error={formState.errors.password?.message}>
          {(p) => (
            <div className="relative">
              <Input {...p} type={show ? 'text' : 'password'} autoComplete="current-password" className="pr-10" {...register('password')} />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-subtle hover:text-ink" aria-label={show ? 'Hide password' : 'Show password'}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          )}
        </Field>
        {error && <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">{error}</p>}
        <Button type="submit" className="w-full justify-center" loading={formState.isSubmitting}>Sign in</Button>
      </form>
      <p className="mt-6 text-center text-sm text-ink-muted">
        New to StopLord? <Link to="/register" className="font-medium text-brand-ink hover:underline">Create a company workspace</Link>
      </p>
    </AuthLayout>
  );
}
